import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from '../utils/SimpleRouter';
import { toast } from 'sonner';
import { getToken, removeToken } from '../utils/storage';

// Public routes that should NOT trigger token expiry redirects
const PUBLIC_ROUTES = [
  '/display/',
  '/submit/',
  '/mobile/',
  '/landing',
  '/bestill',
  '/login',
  '/reset-password',
  '/screensaver-preview'
];

/**
 * Custom hook to monitor token expiry and handle automatic logout
 * - Checks token expiry every minute
 * - Shows warning 5 minutes before expiry
 * - Automatically logs out when token expires
 * - DOES NOT run on public routes (display screens, submit pages, etc.)
 */
const useTokenExpiry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const warningShownRef = useRef(false);
  const checkIntervalRef = useRef(null);

  // Check if current route is a public route
  const isPublicRoute = () => {
    const currentPath = location.pathname;
    return PUBLIC_ROUTES.some(route => currentPath.startsWith(route));
  };

  const decodeToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  };

  const checkTokenExpiry = () => {
    // CRITICAL: Skip token check on public routes (display screens, etc.)
    if (isPublicRoute()) {
      return;
    }
    
    const token = getToken();
    
    if (!token) {
      return;
    }

    const decoded = decodeToken(token);
    
    if (!decoded || !decoded.exp) {
      return;
    }

    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    const expiryTime = decoded.exp;
    const timeUntilExpiry = expiryTime - currentTime;

    // If token has already expired
    if (timeUntilExpiry <= 0) {
      removeToken();
      toast.error('Din økt har utløpt. Vennligst logg inn på nytt.');
      navigate('/login');
      return;
    }

    // Show warning 5 minutes (300 seconds) before expiry
    if (timeUntilExpiry <= 300 && !warningShownRef.current) {
      warningShownRef.current = true;
      const minutesLeft = Math.ceil(timeUntilExpiry / 60);
      toast.warning(`Din økt utløper om ${minutesLeft} minutter. Vennligst lagre arbeidet ditt.`);
    }

    // Reset warning if we're beyond the warning threshold (e.g., after a page reload)
    if (timeUntilExpiry > 300) {
      warningShownRef.current = false;
    }
  };

  useEffect(() => {
    // Don't run token monitoring on public routes
    if (isPublicRoute()) {
      // Clear any existing interval
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }
    
    // Check immediately on mount
    checkTokenExpiry();

    // Check every minute (60000 ms)
    checkIntervalRef.current = setInterval(checkTokenExpiry, 60000);

    // Cleanup on unmount
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [navigate, location.pathname]);

  return null;
};

export default useTokenExpiry;
